import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAbonnement } from '../context/AbonnementContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useResponsive } from '../hooks/useResponsive';
import logo from '../logo.png';
import fleur from '../fleur.png';
import { FaBars, FaTimes } from 'react-icons/fa';
import api from '../services/api';
import { pushSupporte, etatAutorisation, activerPush, synchroniserPush, estIOS, estModeApplication } from '../services/push';

// Pages publiques accessibles aux membres connectés
const PAGES_PUBLIQUES = [
    { path: '/', label: 'Accueil' },
    { path: '/about', label: 'À propos' },
    { path: '/fonctionnalites', label: 'Fonctionnalités' },
    { path: '/comment', label: 'Comment ça marche' },
    { path: '/contact', label: 'Contact' },
];

// Affiche une date sous forme lisible : "il y a 5 min", "hier"...
// Volontairement approximatif : dans une notification, la minute exacte
// n'apporte rien, la fraicheur relative si.
const tempsRelatif = (date) => {
    if (!date) return '';
    const secondes = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (secondes < 60) return "a l'instant";
    const minutes = Math.floor(secondes / 60);
    if (minutes < 60) return `il y a ${minutes} min`;
    const heures = Math.floor(minutes / 60);
    if (heures < 24) return `il y a ${heures} h`;
    const jours = Math.floor(heures / 24);
    if (jours === 1) return 'hier';
    if (jours < 7) return `il y a ${jours} jours`;
    return new Date(date).toLocaleDateString('fr-FR');
};

// Repli quand aucune photo n'est disponible : silhouette neutre.
// Remplace les emojis mariés/mariée, qui n'ont rien à voir avec l'avatar
// choisi par le membre et changeaient d'une page à l'autre.
const IconeAvatar = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke="#c0306a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);

const IconeCloche = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
);

const NavbarAuth = ({ myPhoto, unreadCount = 0, onMessagesClick }) => {
    const { user, logout } = useAuth();
    const { requis, abonnement, actif } = useAbonnement();
    const navigate = useNavigate();
    const location = useLocation();
    const { isMobile } = useResponsive();
    const [menuOpen, setMenuOpen] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [decouvrirOpen, setDecouvrirOpen] = useState(false);
    const [decouvrirPos, setDecouvrirPos] = useState({ left: 0 });

    // ═══ Notifications ═══
    // Contrairement a `unreadCount` (passe en prop par la page parente),
    // la cloche va chercher ses donnees elle-meme. Elle fonctionne donc
    // sur toutes les pages ou la barre est affichee, sans rien y modifier.
    const [notifs, setNotifs] = useState([]);
    const [notifCount, setNotifCount] = useState(0);
    const [notifOpen, setNotifOpen] = useState(false);
    const [notifPos, setNotifPos] = useState({ left: 0 });

    // Etat du push sur cet appareil : 'default', 'granted', 'denied'
    // ou 'unsupported'. Relu a chaque ouverture du panneau, car
    // l'utilisateur peut le changer dans les reglages du navigateur.
    const [pushEtat, setPushEtat] = useState('unsupported');
    const [pushMessage, setPushMessage] = useState('');

    // Suivi des demandes acceptees ou refusees directement depuis la cloche.
    // Cle = id de la notification, valeur = { encours, fini, message }.
    const [demandeEtat, setDemandeEtat] = useState({});

    // Expéditeurs dont la demande est encore en attente de réponse.
    // null tant qu'on ne sait pas : dans ce cas on affiche les boutons,
    // le serveur restant de toute façon seul juge de ce qui est permis.
    const [demandesEnAttente, setDemandesEnAttente] = useState(null);

    // Photo du membre connecté. Comme pour la pastille des messages, la prop
    // `myPhoto` n'est fournie que par certaines pages : sur l'accueil et les
    // pages « Découvrir », l'avatar retombait sur une icône générique.
    const [photoAuto, setPhotoAuto] = useState(null);
    const photoAffichee = myPhoto || photoAuto;

    // Messages non lus, relevés par la barre elle-même.
    // La prop `unreadCount` n'est fournie que par certaines pages : sur les
    // autres (Abonnement, Contact...), la pastille disparaissait alors que des
    // messages attendaient. On garde la prop comme valeur de départ, puis on
    // prend le relais dès que le serveur a répondu.
    const [messagesNonLus, setMessagesNonLus] = useState(null);
    const badgeMessages = messagesNonLus !== null ? messagesNonLus : unreadCount;

    // Sans abonnement valide, les autres pages sont inaccessibles :
    // on masque les liens pour ne pas renvoyer l'utilisateur dans le vide.
    // Les administrateurs ne sont jamais bloqués.
    const bloque = !actif && user?.role !== 'admin';

    const isActive = (path) => location.pathname === path;

    // Ferme le menu déroulant au clic extérieur
    useEffect(() => {
        if (!decouvrirOpen) return;
        const handleClickOutside = () => setDecouvrirOpen(false);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [decouvrirOpen]);

    // ═══ Compteur de notifications ═══
    // Releve au chargement puis toutes les 60 secondes. Les erreurs sont
    // ignorees en silence : une cloche muette vaut mieux qu'un message
    // d'erreur sur toutes les pages du site.
    useEffect(() => {
        if (!user) return undefined;
        let actif = true;
        const releverCompteur = async () => {
            try {
                const res = await api.get('/notifications/unread-count');
                if (actif) setNotifCount(res.data?.total || 0);
            } catch (err) {
                // silencieux
            }
            try {
                const res = await api.get('/messages/unread-count');
                if (actif) setMessagesNonLus(res.data?.total || 0);
            } catch (err) {
                // silencieux
            }
        };
        releverCompteur();

        // Photo de profil : un seul appel au chargement, elle ne change pas
        // en cours de navigation.
        (async () => {
            try {
                const res = await api.get(`/profiles/${user.id}`);
                if (actif && res.data?.photo_url) setPhotoAuto(res.data.photo_url);
            } catch (err) {
                // silencieux : l'icône neutre prend le relais
            }
        })();

        // Repare en silence un abonnement push disparu de la base alors que
        // le navigateur a garde l'autorisation.
        synchroniserPush();
        const timer = setInterval(releverCompteur, 60000);
        return () => { actif = false; clearInterval(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Ferme le panneau des notifications au clic exterieur
    useEffect(() => {
        if (!notifOpen) return undefined;
        const fermer = () => setNotifOpen(false);
        document.addEventListener('click', fermer);
        return () => document.removeEventListener('click', fermer);
    }, [notifOpen]);

    const chargerNotifs = async () => {
        try {
            const res = await api.get('/notifications');
            setNotifs(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setNotifs([]);
        }

        // Une demande peut avoir été acceptée ou refusée depuis l'annuaire,
        // ou avoir expiré. Sans cette vérification, la cloche continuerait
        // de proposer « Accepter / Refuser » sur une demande déjà traitée.
        try {
            const recues = await api.get('/requests/received');
            setDemandesEnAttente(new Set((recues.data || []).map(d => Number(d.sender_id))));
        } catch (err) {
            setDemandesEnAttente(null);
        }
    };

    // stopPropagation indispensable : sans lui, le clic qui ouvre le panneau
    // remonterait jusqu'au document et le refermerait aussitot.
    const toggleNotifs = (e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        // Panneau aligne a droite du bouton, sans jamais sortir de l'ecran.
        setNotifPos({ left: Math.max(8, Math.min(rect.right - 340, window.innerWidth - 348)) });
        const ouvrir = !notifOpen;
        setNotifOpen(ouvrir);
        if (ouvrir) {
            chargerNotifs();
            setPushEtat(pushSupporte() ? etatAutorisation() : 'unsupported');
            setPushMessage('');
        }
    };

    // Mise a jour optimiste : l'interface reagit immediatement, l'appel
    // serveur suit. Si l'appel echoue, le compteur se recalera au prochain
    // relevé automatique.
    const handleNotifClick = async (n) => {
        setNotifOpen(false);
        setMenuOpen(false);
        if (!n.is_read) {
            setNotifs(prev => prev.map(x => (x.id === n.id ? { ...x, is_read: true } : x)));
            setNotifCount(c => Math.max(0, c - 1));
            try { await api.put(`/notifications/${n.id}/read`); } catch (err) { /* silencieux */ }
        }
        if (n.lien) navigate(n.lien);
    };

    const handleActiverPush = async (e) => {
        e.stopPropagation();
        setPushMessage('Activation en cours...');
        const r = await activerPush();
        setPushMessage(r.message);
        setPushEtat(etatAutorisation());
    };

    // Sur mobile, la liste s'ouvre en plein ecran plutot que de se deplier
    // sous le bouton : le menu est deja long, et une liste imbriquee y est
    // illisible.
    const ouvrirNotifsMobile = (e) => {
        e.stopPropagation();
        setMenuOpen(false);
        setNotifOpen(true);
        chargerNotifs();
        setPushEtat(pushSupporte() ? etatAutorisation() : 'unsupported');
        setPushMessage('');
    };

    // Repondre a une demande sans quitter la cloche.
    // La notification ne stocke que l'expediteur, pas l'identifiant de la
    // demande : on retrouve celle-ci dans la liste des demandes recues.
    const repondreDemande = async (e, n, action) => {
        e.stopPropagation();
        setDemandeEtat(prev => ({ ...prev, [n.id]: { encours: true, message: '' } }));
        try {
            const recues = await api.get('/requests/received');
            const demande = (recues.data || []).find(d => d.sender_id === n.acteur_id);

            if (!demande) {
                // Demande expiree (48h), deja traitee, ou relation rompue entre-temps.
                setDemandeEtat(prev => ({
                    ...prev,
                    [n.id]: { fini: true, message: "Cette demande n'est plus disponible." },
                }));
                return;
            }

            await api.put(`/requests/${demande.id}/${action}`);
            setDemandesEnAttente(prev => {
                if (!prev) return prev;
                const copie = new Set(prev);
                copie.delete(Number(n.acteur_id));
                return copie;
            });
            setDemandeEtat(prev => ({
                ...prev,
                [n.id]: {
                    fini: true,
                    message: action === 'accept' ? 'Demande acceptée.' : 'Demande refusée.',
                },
            }));

            if (!n.is_read) {
                setNotifs(prev => prev.map(x => (x.id === n.id ? { ...x, is_read: true } : x)));
                setNotifCount(c => Math.max(0, c - 1));
                api.put(`/notifications/${n.id}/read`).catch(() => { });
            }
        } catch (err) {
            // Le serveur a ses propres regles (exclusivite notamment) :
            // on affiche son message plutot qu'une erreur generique.
            setDemandeEtat(prev => ({
                ...prev,
                [n.id]: { fini: false, message: err.response?.data?.message || "L'action n'a pas abouti." },
            }));
        }
    };

    const handleToutLire = async (e) => {
        e.stopPropagation();
        setNotifs(prev => prev.map(x => ({ ...x, is_read: true })));
        setNotifCount(0);
        try { await api.put('/notifications/read-all'); } catch (err) { /* silencieux */ }
    };

    // Menu mobile ouvert : on fige le défilement de la page derrière,
    // pour qu'elle ne scrolle plus et reste cachée sous le menu plein écran.
    useEffect(() => {
        if (menuOpen || (isMobile && notifOpen)) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen, isMobile, notifOpen]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const openLogoutModal = () => {
        setMenuOpen(false);
        setShowLogoutModal(true);
    };

    const handleMessagesClick = () => {
        if (onMessagesClick) {
            onMessagesClick();
        } else {
            navigate('/messages');
        }
    };

    // ═══ Alerte abonnement ═══
    // Nombre de jours restants avant expiration
    const joursRestants = abonnement?.date_fin
        ? Math.ceil((new Date(abonnement.date_fin) - new Date()) / (1000 * 60 * 60 * 24))
        : null;

    // Badge rouge : abonnement requis mais absent
    const abonnementManquant = requis && !abonnement;
    // Badge doré : expiration proche (7 jours ou moins)
    const expirationProche = abonnement && joursRestants !== null && joursRestants <= 7;

    const libelleAbonnement = () => {
        if (abonnementManquant) return 'S\'abonner';
        if (abonnement) return 'Mon abonnement';
        return 'Abonnement';
    };

    const avatar = (
        <div style={styles.navAvatar}>
            {photoAffichee ? (
                <img src={photoAffichee} alt="profil" style={styles.navAvatarImg} />
            ) : (
                <IconeAvatar />
            )}
        </div>
    );

    // Sur iPhone, Safari n'expose le push que si le site a ete ajoute a
    // l'ecran d'accueil. Dans un onglet normal, pushSupporte() renvoie donc
    // false et le membre ne verrait rien du tout : on lui explique la
    // manipulation au lieu de lui proposer un bouton inoperant.
    const iosAInstaller = estIOS() && !estModeApplication();
    const proposerActivation = !iosAInstaller && pushSupporte() && pushEtat !== 'granted';

    // Contenu de la liste, partage entre le panneau (ordinateur)
    // et la modale plein ecran (mobile).
    const rendreNotifs = (avecTitre = true, clair = false) => (
        <>
            <div style={clair ? styles.notifHeaderClair : styles.notifHeader}>
                {avecTitre && (
                    <span style={clair ? styles.notifTitreClair : styles.notifTitre}>Notifications</span>
                )}
                {notifCount > 0 && (
                    <button
                        onClick={handleToutLire}
                        style={clair ? styles.notifToutLireClair : styles.notifToutLire}
                    >
                        Tout marquer comme lu
                    </button>
                )}
            </div>
            {/* Proposition d'activation du push : uniquement si l'appareil
                le gere et si l'autorisation n'a pas deja ete donnee. */}
            {iosAInstaller && (
                <div style={{
                    ...(clair ? styles.notifPushClair : styles.notifPush),
                    flexDirection: 'column', alignItems: 'flex-start', gap: '6px',
                }}>
                    <span style={{
                        ...(clair ? styles.notifPushTexteClair : styles.notifPushTexte),
                        fontWeight: '700',
                    }}>
                        Recevoir les notifications sur votre iPhone
                    </span>
                    <span style={clair ? styles.notifPushTexteClair : styles.notifPushTexte}>
                        1. Appuyez sur le bouton Partager, en bas de Safari.<br />
                        2. Choisissez « Sur l'écran d'accueil ».<br />
                        3. Ouvrez le site depuis cette nouvelle icône, puis revenez ici pour activer.
                    </span>
                </div>
            )}

            {proposerActivation && (
                <div style={clair ? styles.notifPushClair : styles.notifPush}>
                    <span style={clair ? styles.notifPushTexteClair : styles.notifPushTexte}>
                        Recevoir les notifications sur cet appareil, meme site ferme.
                    </span>
                    {pushEtat !== 'denied' && (
                        <button
                            onClick={handleActiverPush}
                            style={clair ? styles.notifPushBtnClair : styles.notifPushBtn}
                        >
                            Activer
                        </button>
                    )}
                </div>
            )}
            {pushMessage && (
                <p style={clair ? styles.notifPushMessageClair : styles.notifPushMessage}>{pushMessage}</p>
            )}

            {notifs.length === 0 ? (
                <p style={clair ? styles.notifVideClair : styles.notifVide}>
                    Aucune notification pour le moment.
                </p>
            ) : (
                notifs.map(n => {
                    const etat = demandeEtat[n.id] || {};
                    const encoreOuverte = demandesEnAttente === null
                        || demandesEnAttente.has(Number(n.acteur_id));
                    const repondable = n.type === 'demande_recue' && n.acteur_id
                        && !etat.fini && encoreOuverte;

                    return (
                        <div
                            key={n.id}
                            onClick={() => handleNotifClick(n)}
                            style={n.is_read
                                ? styles.notifItem
                                : { ...styles.notifItem, ...(clair ? styles.notifItemNonLuClair : styles.notifItemNonLu) }}
                        >
                            <span style={n.is_read
                                ? styles.notifPucePlaceholder
                                : (clair ? styles.notifPuceClair : styles.notifPuce)} />
                            <span style={styles.notifTexte}>
                                <span style={clair ? styles.notifItemTitreClair : styles.notifItemTitre}>{n.titre}</span>
                                {n.contenu && (
                                    <span style={clair ? styles.notifItemContenuClair : styles.notifItemContenu}>
                                        {n.contenu}
                                    </span>
                                )}
                                <span style={clair ? styles.notifDateClair : styles.notifDate}>
                                    {tempsRelatif(n.created_at)}
                                </span>

                                {repondable && (
                                    <span style={styles.notifActions}>
                                        <button
                                            onClick={(e) => repondreDemande(e, n, 'accept')}
                                            disabled={etat.encours}
                                            style={clair ? styles.notifBtnAccepterClair : styles.notifBtnAccepter}
                                        >
                                            {etat.encours ? '...' : 'Accepter'}
                                        </button>
                                        <button
                                            onClick={(e) => repondreDemande(e, n, 'reject')}
                                            disabled={etat.encours}
                                            style={clair ? styles.notifBtnRefuserClair : styles.notifBtnRefuser}
                                        >
                                            Refuser
                                        </button>
                                    </span>
                                )}

                                {etat.message && (
                                    <span style={clair ? styles.notifEtatClair : styles.notifEtat}>
                                        {etat.message}
                                    </span>
                                )}
                            </span>
                        </div>
                    );
                })
            )}
        </>
    );

    return (
        <>
            <nav style={{
                ...styles.navbar,
                height: isMobile ? '110px' : '82px',
                padding: isMobile ? '0 16px' : '0 60px',
                backgroundImage: isMobile ? 'none' : `url(${fleur})`,
                justifyContent: isMobile ? 'center' : 'space-between',
            }}>
                <img
                    src={logo}
                    alt="logo"
                    onClick={() => navigate(bloque ? '/abonnement' : '/dashboard')}
                    style={{
                        ...styles.navLogo,
                        height: isMobile ? '100px' : '70px',
                        width: 'auto',
                        maxWidth: isMobile ? '75%' : 'none',
                        objectFit: 'contain',
                        marginLeft: isMobile ? 0 : '50px',
                        marginTop: 0,
                        cursor: 'pointer',
                    }}
                />
                {isMobile ? (
                    <button onClick={() => setMenuOpen(!menuOpen)} style={styles.burger}>
                        {menuOpen ? <FaTimes color="white" size={24} /> : <FaBars color="white" size={24} />}
                        {abonnementManquant && <span style={styles.burgerDot} />}
                    </button>
                ) : (
                    <div style={styles.navRight}>
                        <div style={styles.navUserBox}>
                            {avatar}
                            <span style={styles.navUser}>{user?.first_name || user?.email}</span>
                        </div>
                        {!bloque && (
                            <>
                                <button title="Découvrir les profils des membres" onClick={() => navigate('/dashboard')} style={isActive('/dashboard') ? styles.tabActive : styles.tab}>Les profils</button>
                                <button title="Voir mon profil" onClick={() => navigate('/profile')} style={isActive('/profile') ? styles.tabActive : styles.tab}>Mon Profil</button>
                                <button
                                    data-messenger-toggle
                                    title="Voir mes messages"
                                    onClick={handleMessagesClick}
                                    style={isActive('/messages') ? styles.tabActive : styles.tab}
                                >
                                    Messages
                                    {badgeMessages > 0 && <span style={styles.navBadge}>{badgeMessages}</span>}
                                </button>
                            </>
                        )}
                        <button
                            title={abonnementManquant ? 'Un abonnement est nécessaire' : 'Gérer mon abonnement'}
                            onClick={() => navigate('/abonnement')}
                            style={
                                isActive('/abonnement') ? styles.tabActive
                                    : abonnementManquant ? styles.tabAlerte
                                        : styles.tab
                            }
                        >
                            {libelleAbonnement()}
                            {abonnementManquant && <span style={styles.navBadgeRouge}>!</span>}
                            {expirationProche && (
                                <span style={{ ...styles.navBadge, backgroundColor: '#ffffff', color: '#4a0028' }}>
                                    {joursRestants}j
                                </span>
                            )}
                        </button>
                        {/* Pages publiques regroupées dans un menu déroulant */}
                        <div style={styles.dropdownWrap}>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    // Le menu étant en position fixe, on l'aligne sur le bouton
                                    const r = e.currentTarget.getBoundingClientRect();
                                    setDecouvrirPos({ left: r.left });
                                    setDecouvrirOpen(!decouvrirOpen);
                                }}
                                style={PAGES_PUBLIQUES.some(p => isActive(p.path)) ? styles.tabActive : styles.tab}
                                title="Pages du site"
                            >
                                Découvrir
                                <span style={{ marginLeft: '6px', fontSize: '10px' }}>
                                    {decouvrirOpen ? '▲' : '▼'}
                                </span>
                            </button>
                            {decouvrirOpen && (
                                <div style={{ ...styles.dropdown, left: decouvrirPos.left }}>
                                    {PAGES_PUBLIQUES.map(p => (
                                        <button
                                            key={p.path}
                                            onClick={() => { navigate(p.path); setDecouvrirOpen(false); }}
                                            style={isActive(p.path) ? styles.dropdownItemActive : styles.dropdownItem}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* ═══ Cloche de notifications ═══
                            Placee hors du bloc `!bloque` : un membre sans abonnement
                            doit quand meme etre prevenu que son profil est valide. */}
                        <div style={styles.dropdownWrap}>
                            <button
                                onClick={toggleNotifs}
                                style={{ ...(notifOpen ? styles.tabActive : styles.tab), display: 'flex', alignItems: 'center' }}
                                title="Mes notifications"
                            >
                                <IconeCloche />
                                {notifCount > 0 && (
                                    <span style={styles.navBadgeRouge}>{notifCount > 9 ? '9+' : notifCount}</span>
                                )}
                            </button>
                            {notifOpen && (
                                <div
                                    style={{ ...styles.notifPanel, left: notifPos.left }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {rendreNotifs()}
                                </div>
                            )}
                        </div>

                        <button onClick={openLogoutModal} style={styles.logoutBtn} title="Déconnexion">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                                <line x1="12" y1="2" x2="12" y2="12" />
                            </svg>
                        </button>
                    </div>
                )}
            </nav>

            {isMobile && menuOpen && (
                <div style={styles.mobileMenu}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: '4px' }}>
                        <button
                            onClick={() => setMenuOpen(false)}
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}
                            title="Fermer le menu"
                        >
                            <FaTimes color="white" size={24} />
                        </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0' }}>
                        {avatar}
                        <span style={{ color: '#c0306a', fontWeight: '600' }}>{user?.first_name || user?.email}</span>
                    </div>
                    {!bloque && (
                        <>
                            <button
                                onClick={() => { navigate('/dashboard'); setMenuOpen(false); }}
                                style={isActive('/dashboard') ? styles.mobileBtnActive : styles.mobileBtn}
                            >
                                Les profils
                            </button>
                            <button
                                onClick={() => { navigate('/profile'); setMenuOpen(false); }}
                                style={isActive('/profile') ? styles.mobileBtnActive : styles.mobileBtn}
                            >
                                Mon Profil
                            </button>
                            <button
                                data-messenger-toggle
                                onClick={() => { handleMessagesClick(); setMenuOpen(false); }}
                                style={isActive('/messages') ? styles.mobileBtnActive : styles.mobileBtn}
                            >
                                Messages
                                {badgeMessages > 0 && <span style={styles.mobileBadge}>{badgeMessages}</span>}
                            </button>
                        </>
                    )}
                    {/* Notifications : hors du bloc `!bloque`, meme raison que sur ordinateur */}
                    <button onClick={ouvrirNotifsMobile} style={styles.mobileBtn}>
                        Notifications
                        {notifCount > 0 && <span style={styles.mobileBadgeRouge}>{notifCount}</span>}
                    </button>

                    <button
                        onClick={() => { navigate('/abonnement'); setMenuOpen(false); }}
                        style={
                            isActive('/abonnement') ? styles.mobileBtnActive
                                : abonnementManquant ? styles.mobileBtnAlerte
                                    : styles.mobileBtn
                        }
                    >
                        {libelleAbonnement()}
                        {abonnementManquant && <span style={styles.mobileBadgeRouge}>!</span>}
                        {expirationProche && (
                            <span style={{ ...styles.mobileBadge, backgroundColor: '#ffffff', color: '#4a0028' }}>
                                {joursRestants}j
                            </span>
                        )}
                    </button>

                    {/* Pages publiques */}
                    <div style={styles.mobileSeparateur}>Découvrir</div>
                    {PAGES_PUBLIQUES.map(p => (
                        <button
                            key={p.path}
                            onClick={() => { navigate(p.path); setMenuOpen(false); }}
                            style={isActive(p.path) ? styles.mobileBtnActive : styles.mobileBtn}
                        >
                            {p.label}
                        </button>
                    ))}

                    <button onClick={openLogoutModal} style={styles.mobileBtn}>Déconnexion</button>
                </div>
            )}

            {/* ═══ PANNEAU DES NOTIFICATIONS (MOBILE) ═══
                Calque sur le panneau Messenger : plein ecran, fond rose clair,
                en-tete avec titre et croix de fermeture. */}
            {isMobile && notifOpen && (
                <div style={styles.notifPanelPlein} onClick={(e) => e.stopPropagation()}>
                    <div style={styles.notifPanelHeader}>
                        <h3 style={styles.notifPanelTitre}>Notifications</h3>
                        <button
                            onClick={() => setNotifOpen(false)}
                            style={styles.notifPanelClose}
                            title="Fermer"
                        >
                            <FaTimes color="#4a0028" size={16} />
                        </button>
                    </div>
                    <div style={styles.notifPanelBody}>
                        {rendreNotifs(false, true)}
                    </div>
                </div>
            )}

            {/* ═══ MODALE DE CONFIRMATION DE DÉCONNEXION ═══ */}
            {showLogoutModal && (
                <div style={styles.modalOverlay} onClick={() => setShowLogoutModal(false)}>
                    <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalIcon}>👋</div>
                        <h3 style={styles.modalTitle}>Déconnexion</h3>
                        <p style={styles.modalText}>
                            Voulez-vous vraiment vous déconnecter ?
                        </p>
                        <div style={styles.modalActions}>
                            <button onClick={() => setShowLogoutModal(false)} style={styles.modalCancel}>
                                Annuler
                            </button>
                            <button onClick={handleLogout} style={styles.modalConfirm}>
                                Déconnecter
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const styles = {
    navbar: {
        width: '100%',
        boxSizing: 'border-box',
        height: '72px', overflow: 'hidden', display: 'flex', alignItems: 'center',
        position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
        backgroundColor: '#4a0028', backgroundSize: 'auto 120%', backgroundPosition: 'left center', backgroundRepeat: 'no-repeat',
    },
    navLogo: { width: 'auto' },
    burger: { background: 'transparent', border: 'none', cursor: 'pointer', position: 'absolute', right: '16px' },
    burgerDot: {
        position: 'absolute', top: '-2px', right: '-2px',
        width: '10px', height: '10px', borderRadius: '50%',
        backgroundColor: '#c0306a', border: '2px solid #4a0028',
    },
    navRight: { display: 'flex', alignItems: 'center', gap: '12px' },
    navUserBox: { display: 'flex', alignItems: 'center', gap: '10px' },
    navAvatar: {
        width: '38px', height: '38px', borderRadius: '50%', overflow: 'hidden',
        border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#fde8f0', flexShrink: 0,
    },
    navAvatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
    navUser: { color: 'white', fontWeight: '600', fontSize: '14px' },
    dropdownWrap: { display: 'inline-block' },
    dropdown: {
        // Position fixe : la navbar a overflow:hidden (pour rogner le logo),
        // ce qui masquerait un menu en position absolue.
        position: 'fixed',
        top: '76px',
        minWidth: '190px',
        backgroundColor: '#2a0014',
        border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: '10px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        padding: '6px',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    dropdownItem: {
        padding: '10px 14px',
        background: 'transparent',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '13.5px',
        textAlign: 'left',
        whiteSpace: 'nowrap',
    },
    dropdownItemActive: {
        padding: '10px 14px',
        background: 'rgba(255,255,255,0.15)',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '13.5px',
        fontWeight: '700',
        textAlign: 'left',
        whiteSpace: 'nowrap',
    },
    mobileSeparateur: {
        color: '#c0306a',
        fontSize: '11.5px',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        padding: '12px 4px 4px',
        borderTop: '1px solid rgba(255,255,255,0.15)',
        marginTop: '6px',
    },
    logoutBtn: {
        padding: '8px 12px',
        backgroundColor: 'transparent',
        border: '1px solid rgba(255,255,255,0.5)',
        borderRadius: '6px',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '38px',
        boxSizing: 'border-box',
        flexShrink: 0,
    },
    tab: {
        position: 'relative',
        padding: '8px 16px',
        backgroundColor: 'transparent',
        color: 'white',
        border: '1px solid rgba(255,255,255,0.5)',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '500',
    },
    tabActive: {
        position: 'relative',
        padding: '8px 16px',
        backgroundColor: '#2a0014',
        color: 'white',
        border: '1px solid white',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '700',
    },
    tabAlerte: {
        position: 'relative',
        padding: '8px 16px',
        background: 'linear-gradient(135deg, #c9a84c, #b8942f)',
        color: '#4a0028',
        border: '1px solid #c9a84c',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '700',
    },
    navBadge: {
        position: 'absolute', top: '-6px', right: '-6px',
        backgroundColor: '#c0306a', color: '#ffffff',
        borderRadius: '50%', minWidth: '18px', height: '18px',
        fontSize: '11px', fontWeight: '700',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 4px', border: '2px solid #4a0028',
    },
    navBadgeRouge: {
        position: 'absolute', top: '-6px', right: '-6px',
        backgroundColor: '#c0306a', color: 'white',
        borderRadius: '50%', minWidth: '18px', height: '18px',
        fontSize: '11px', fontWeight: '800',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 4px', border: '2px solid #4a0028',
    },
    // ═══ Panneau de notifications ═══
    notifPanel: {
        // Position fixe pour la meme raison que le menu Decouvrir :
        // la navbar a overflow:hidden et rognerait un panneau en absolu.
        position: 'fixed',
        top: '76px',
        width: '340px',
        maxHeight: '420px',
        overflowY: 'auto',
        backgroundColor: '#2a0014',
        border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: '10px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        padding: '6px',
        zIndex: 1000,
        boxSizing: 'border-box',
    },
    // ═══ Panneau plein ecran (mobile), calque sur le panneau Messenger ═══
    notifPanelPlein: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        height: '100dvh',
        maxWidth: '100vw',
        background: 'linear-gradient(135deg, #fdf2f5, #faeef2)',
        zIndex: 1001,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
    },
    notifPanelHeader: {
        padding: '18px 20px 12px 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '1px solid #ecd9b9', flexShrink: 0,
    },
    notifPanelTitre: { color: '#4a0028', fontSize: '18px', fontWeight: '700', margin: 0 },
    notifPanelClose: {
        background: 'rgba(74,0,40,0.06)', border: 'none', borderRadius: '50%',
        width: '32px', height: '32px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    notifPanelBody: { flex: 1, overflowY: 'auto', padding: '10px 10px 24px' },

    // Variantes claires : memes elements, lisibles sur le fond rose.
    notifHeaderClair: {
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        gap: '10px', padding: '4px 8px 10px',
    },
    notifTitreClair: {
        color: '#4a0028', fontSize: '11px', fontWeight: '700',
        letterSpacing: '0.6px', textTransform: 'uppercase',
    },
    notifToutLireClair: {
        background: 'transparent', border: 'none', color: '#c0306a',
        fontSize: '11.5px', cursor: 'pointer', textDecoration: 'underline', padding: 0,
    },
    notifItemNonLuClair: { backgroundColor: 'rgba(192,48,106,0.09)' },
    notifPuceClair: {
        width: '8px', height: '8px', borderRadius: '50%',
        backgroundColor: '#c0306a', marginTop: '5px', flexShrink: 0,
    },
    notifItemTitreClair: { color: '#4a0028', fontSize: '13.5px', fontWeight: '700', lineHeight: '1.3' },
    notifItemContenuClair: { color: '#6b4a5a', fontSize: '12.5px', lineHeight: '1.45' },
    notifDateClair: { color: '#9a7d88', fontSize: '11px' },
    notifVideClair: {
        color: '#9a7d88', fontSize: '13px',
        padding: '28px 12px', textAlign: 'center', margin: 0,
    },
    notifPushClair: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '10px', padding: '12px 14px', margin: '0 4px 8px',
        backgroundColor: 'rgba(74,0,40,0.05)', border: '1px solid #ecd9b9',
        borderRadius: '10px',
    },
    notifPushTexteClair: { color: '#6b4a5a', fontSize: '12px', lineHeight: '1.45' },
    notifPushBtnClair: {
        flexShrink: 0, padding: '8px 14px', borderRadius: '8px',
        backgroundColor: '#4a0028', color: '#ffffff', border: 'none',
        fontSize: '12px', fontWeight: '700', cursor: 'pointer',
    },
    notifPushMessageClair: {
        color: '#6b4a5a', fontSize: '12px',
        padding: '0 14px 10px', margin: 0, lineHeight: '1.45',
    },
    notifHeader: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '10px', padding: '8px 10px',
        borderBottom: '1px solid rgba(255,255,255,0.15)', marginBottom: '4px',
    },
    notifTitre: {
        color: '#ffffff', fontSize: '11px', fontWeight: '700',
        letterSpacing: '0.6px', textTransform: 'uppercase',
    },
    notifToutLire: {
        background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.7)',
        fontSize: '11px', cursor: 'pointer', textDecoration: 'underline', padding: 0,
    },
    notifPush: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '10px', padding: '10px 12px', margin: '0 0 4px',
        backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: '8px',
    },
    notifPushTexte: { color: 'rgba(255,255,255,0.8)', fontSize: '11.5px', lineHeight: '1.4' },
    notifPushBtn: {
        flexShrink: 0, padding: '6px 12px', borderRadius: '6px',
        backgroundColor: '#ffffff', color: '#4a0028', border: 'none',
        fontSize: '11.5px', fontWeight: '700', cursor: 'pointer',
    },
    notifPushMessage: {
        color: 'rgba(255,255,255,0.7)', fontSize: '11.5px',
        padding: '0 12px 8px', margin: 0, lineHeight: '1.45',
    },
    notifItem: {
        display: 'flex', gap: '10px', width: '100%', textAlign: 'left',
        background: 'transparent', border: 'none', borderRadius: '8px',
        padding: '10px 12px', cursor: 'pointer', alignItems: 'flex-start',
        boxSizing: 'border-box',
    },
    notifItemNonLu: { backgroundColor: 'rgba(201,168,76,0.12)' },
    notifActions: { display: 'flex', gap: '8px', marginTop: '8px' },
    notifBtnAccepter: {
        padding: '6px 14px', borderRadius: '6px', border: 'none',
        backgroundColor: '#ffffff', color: '#4a0028',
        fontSize: '11.5px', fontWeight: '700', cursor: 'pointer',
    },
    notifBtnRefuser: {
        padding: '6px 14px', borderRadius: '6px',
        border: '1px solid rgba(255,255,255,0.35)',
        backgroundColor: 'transparent', color: 'rgba(255,255,255,0.85)',
        fontSize: '11.5px', fontWeight: '600', cursor: 'pointer',
    },
    notifBtnAccepterClair: {
        padding: '7px 16px', borderRadius: '7px', border: 'none',
        backgroundColor: '#4a0028', color: '#ffffff',
        fontSize: '12px', fontWeight: '700', cursor: 'pointer',
    },
    notifBtnRefuserClair: {
        padding: '7px 16px', borderRadius: '7px', border: '1px solid #d9b8c6',
        backgroundColor: 'transparent', color: '#7a1340',
        fontSize: '12px', fontWeight: '600', cursor: 'pointer',
    },
    notifEtat: { color: '#c9a84c', fontSize: '11.5px', marginTop: '6px', lineHeight: '1.45' },
    notifEtatClair: { color: '#c0306a', fontSize: '12px', marginTop: '6px', lineHeight: '1.45' },
    notifPuce: {
        width: '8px', height: '8px', borderRadius: '50%',
        backgroundColor: '#ffffff', marginTop: '5px', flexShrink: 0,
    },
    notifPucePlaceholder: { width: '8px', flexShrink: 0 },
    notifTexte: { display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 },
    notifItemTitre: { color: 'white', fontSize: '13px', fontWeight: '700', lineHeight: '1.3' },
    notifItemContenu: { color: 'rgba(255,255,255,0.75)', fontSize: '12px', lineHeight: '1.45' },
    notifDate: { color: 'rgba(255,255,255,0.45)', fontSize: '11px' },
    notifVide: {
        color: 'rgba(255,255,255,0.6)', fontSize: '12.5px',
        padding: '18px 12px', textAlign: 'center', margin: 0,
    },

    mobileMenu: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#1a0010', display: 'flex', flexDirection: 'column', padding: '16px', gap: '8px', zIndex: 300, overflowY: 'auto' },
    mobileBtn: {
        position: 'relative',
        padding: '12px',
        backgroundColor: 'transparent',
        color: 'white',
        border: '1px solid rgba(255,255,255,0.5)',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '500',
    },
    mobileBtnActive: {
        position: 'relative',
        padding: '12px',
        backgroundColor: '#2a0014',
        color: 'white',
        border: '1px solid white',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '700',
    },
    mobileBtnAlerte: {
        position: 'relative',
        padding: '12px',
        background: 'linear-gradient(135deg, #c9a84c, #b8942f)',
        color: '#4a0028',
        border: '1px solid #c9a84c',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '700',
    },
    mobileBadge: {
        marginLeft: '8px',
        backgroundColor: '#c0306a', color: '#ffffff',
        borderRadius: '10px', minWidth: '20px', height: '20px',
        fontSize: '12px', fontWeight: '700',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 6px',
    },
    mobileBadgeRouge: {
        marginLeft: '8px',
        backgroundColor: '#c0306a', color: 'white',
        borderRadius: '10px', minWidth: '20px', height: '20px',
        fontSize: '12px', fontWeight: '800',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 6px',
    },

    // ═══ Modale de déconnexion ═══
    modalOverlay: {
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '16px', boxSizing: 'border-box',
    },
    modalBox: {
        backgroundColor: 'white',
        borderRadius: '18px',
        padding: '32px',
        width: '100%',
        maxWidth: '400px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        boxSizing: 'border-box',
        textAlign: 'center',
    },
    modalIcon: {
        fontSize: '42px',
        marginBottom: '12px',
    },
    modalTitle: {
        color: '#1a0010', fontSize: '22px', fontWeight: '800', margin: '0 0 10px',
    },
    modalText: {
        color: '#777', fontSize: '14px', lineHeight: '1.6', margin: '0 0 26px',
    },
    modalActions: {
        display: 'flex', gap: '14px', marginTop: '4px',
    },
    modalCancel: {
        flex: 1,
        textAlign: 'center',
        padding: '12px',
        backgroundColor: 'white',
        color: '#8b1a4a',
        border: '1.5px solid #e8d5e0',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    modalConfirm: {
        flex: 1,
        textAlign: 'center',
        padding: '12px',
        background: 'linear-gradient(135deg, #c0306a, #8b1a4a)',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '700',
        cursor: 'pointer',
        boxShadow: '0 4px 14px rgba(192,48,106,0.35)',
        transition: 'all 0.2s ease',
    },
};

export default NavbarAuth;